if (!customElements.get('supply-finder')) {
  class SupplyFinder extends HTMLElement {
    static requests = new Map();
    static landingPrepared = false;

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.brand = this.querySelector('[data-supply-brand]');
      this.search = this.querySelector('[data-model-search]');
      this.list = this.querySelector('[data-model-list]');
      this.message = this.querySelector('[data-model-message]');
      this.clear = this.querySelector('[data-clear-model]');
      this.params = new URL(window.location.href).searchParams;
      this.selectedModel = this.params.get('filter.p.m.custom.filter_models') || '';
      this.search.value = this.selectedModel;
      this.brand.addEventListener('change', () => window.location.assign(this.destination(this.brand.value)));
      this.search.addEventListener('input', () => this.renderModels());
      this.search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (this.matches?.length === 1) window.location.assign(this.destination(this.brand.value, this.matches[0]));
        }
      });
      queueMicrotask(() => this.retainFilters());
      this.prepareLanding();
      const url = this.dataset.indexUrl;
      if (!SupplyFinder.requests.has(url)) {
        SupplyFinder.requests.set(url, fetch(url).then((response) => {
          if (!response.ok) throw new Error('Supply model lookup failed');
          return response.json();
        }).catch((error) => {
          SupplyFinder.requests.delete(url);
          throw error;
        }));
      }
      SupplyFinder.requests.get(url).then((index) => {
        if (!this.isConnected) return;
        this.index = index.brands.filter((brand) => brand.models[this.dataset.scope]?.length);
        const requested = this.params.getAll('filter.p.m.custom.brand');
        const available = JSON.parse(this.dataset.availableBrands || '[]') || [];
        const inferred = requested.length === 1 ? requested[0] : requested.length === 0 && available.length === 1 ? available[0] : '';
        const current = this.index.some((brand) => brand.name === inferred) ? inferred : '';
        const options = [new Option(this.dataset.allBrands, '')];
        for (const brand of this.index) options.push(new Option(brand.name, brand.name));
        this.brand.replaceChildren(...options);
        this.brand.value = current;
        this.brand.disabled = false;
        this.renderModels();
        this.dataset.ready = 'true';
      }).catch(() => {
        if (!this.isConnected) return;
        this.message.textContent = this.dataset.error;
        this.message.hidden = false;
      });
    }

    prepareLanding() {
      if (SupplyFinder.landingPrepared || this.dataset.scope !== 'all' || window.location.hash || window.Shopify?.designMode) return;
      if (this.params.has('page') || Array.from(this.params.keys()).some((name) => name.startsWith('filter.'))) return;
      if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') return;
      SupplyFinder.landingPrepared = true;
      const positionLanding = () => {
        if (!this.isConnected || window.scrollY > 0) return;
        const banner = document.querySelector('main .collection-hero');
        if (!banner) return;
        const styles = getComputedStyle(document.documentElement);
        const header = parseFloat(styles.getPropertyValue('--header-height')) || document.querySelector('.header-section')?.getBoundingClientRect().height || 0;
        const navigation = window.matchMedia('(min-width: 1024px)').matches ? parseFloat(styles.getPropertyValue('--header-navigation-height')) || 0 : 0;
        const top = banner.getBoundingClientRect().top + window.scrollY - Math.max(0, header - navigation);
        window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' });
      };
      if (document.readyState === 'complete') requestAnimationFrame(positionLanding);
      else window.addEventListener('load', positionLanding, { once: true });
    }

    retainFilters() {
      const form = Array.from(this.parentElement.querySelectorAll('form[data-supply-context]')).find((form) => form.dataset.supplyContext === this.dataset.context);
      const holder = form?.querySelector('[data-supply-retained-filters]');
      if (!holder) return;
      const inputs = [];
      for (const name of ['filter.p.m.custom.brand', 'filter.p.m.custom.filter_models']) {
        for (const value of this.params.getAll(name)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          inputs.push(input);
        }
      }
      holder.replaceChildren(...inputs);
      let count = 0;
      let hasPrice = false;
      for (const [name, value] of this.params) {
        if (!name.startsWith('filter.') || value === '') continue;
        if (name === 'filter.v.price.gte' || name === 'filter.v.price.lte') hasPrice = true;
        else count += 1;
      }
      if (hasPrice) count += 1;
      this.closest('[id^="shopify-section-"]')?.querySelectorAll('facet-count').forEach((element) => {
        element.textContent = `(${count})`;
        element.hidden = count === 0;
      });
    }

    destination(brand, model = '') {
      const url = new URL(this.dataset.baseUrl, window.location.origin);
      if (brand) url.searchParams.set('filter.p.m.custom.brand', brand);
      if (model) url.searchParams.set('filter.p.m.custom.filter_models', model);
      url.hash = 'SupplyResults';
      return url.toString();
    }

    normalize(value) {
      return value.normalize('NFKC').toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
    }

    renderModels() {
      const brand = this.index.find((item) => item.name === this.brand.value);
      this.list.replaceChildren();
      this.search.disabled = !brand;
      this.clear.hidden = !this.selectedModel;
      this.clear.href = this.destination(this.brand.value);
      if (!brand) {
        this.matches = [];
        this.message.textContent = this.dataset.chooseBrand;
        this.message.hidden = false;
        return;
      }
      const query = this.normalize(this.search.value);
      this.matches = brand.models[this.dataset.scope].filter((model) => this.normalize(model).includes(query));
      const fragment = document.createDocumentFragment();
      for (const model of this.matches) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = this.destination(brand.name, model);
        link.textContent = model;
        if (model === this.selectedModel) link.setAttribute('aria-current', 'page');
        item.append(link);
        fragment.append(item);
      }
      this.list.append(fragment);
      this.message.textContent = this.matches.length === 1 ? this.dataset.oneModel : this.matches.length ? this.dataset.modelCount.replace('__COUNT__', String(this.matches.length)) : this.dataset.noMatches;
      this.message.hidden = false;
    }
  }
  customElements.define('supply-finder', SupplyFinder);
}
